import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useCompany } from './CompanyContext';
import { useAuth } from './AuthContext';

/**
 * AudienceContext
 *
 * Provides:
 *   1. Real-time CRUD for companies/{cId}/audiences
 *   2. Real-time snapshots of companies/{cId}/quotes  (for audience engine)
 *   3. Real-time snapshots of companies/{cId}/invoices (for audience engine)
 *
 * Firestore audience document shape:
 * {
 *   name, description, filters, filterLogic, estimatedSize,
 *   createdBy, createdAt, updatedAt
 * }
 */

const AudienceContext = createContext(null);

export const useAudiences = () => {
  const ctx = useContext(AudienceContext);
  if (!ctx) throw new Error('useAudiences must be used within AudienceProvider');
  return ctx;
};

export const AudienceProvider = ({ children }) => {
  const { companyId } = useCompany();
  const { user } = useAuth();

  const [audiences,   setAudiences]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [allQuotes,   setAllQuotes]   = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);

  // ── Audiences listener ────────────────────────────────────────────────────
  useEffect(() => {
    if (!db || !companyId) { setLoading(false); return; }
    const colRef = collection(db, 'companies', companyId, 'audiences');
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        setAudiences(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[AudienceContext] Audiences snapshot error:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [companyId]);

  // ── Quotes listener (for audience engine preview) — LAZY ──────────────────
  // Quotes/invoices are only needed by the Engage audience builder. They are
  // subscribed on demand via ensureAudienceSources() instead of streaming two
  // full collections into every session app-wide.
  const [sourcesRequested, setSourcesRequested] = useState(false);
  const ensureAudienceSources = useCallback(() => setSourcesRequested(true), []);

  useEffect(() => {
    if (!db || !companyId || !sourcesRequested) return;
    const ref = collection(db, 'companies', companyId, 'quotes');
    const unsub = onSnapshot(
      ref,
      (snap) => setAllQuotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn('[AudienceContext] Quotes snapshot error:', err)
    );
    return unsub;
  }, [companyId, sourcesRequested]);

  // ── Invoices listener (for audience engine preview) — LAZY ────────────────
  useEffect(() => {
    if (!db || !companyId || !sourcesRequested) return;
    const ref = collection(db, 'companies', companyId, 'invoices');
    const unsub = onSnapshot(
      ref,
      (snap) => setAllInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn('[AudienceContext] Invoices snapshot error:', err)
    );
    return unsub;
  }, [companyId, sourcesRequested]);

  /**
   * Create a new audience document.
   * Returns the new document ID.
   */
  const createAudience = async ({ name, description, filters, filterLogic, estimatedSize }) => {
    if (!companyId) throw new Error('No companyId');
    const colRef = collection(db, 'companies', companyId, 'audiences');
    const docRef = await addDoc(colRef, {
      name:          name.trim(),
      description:   (description || '').trim(),
      filters:       filters || [],
      filterLogic:   filterLogic || 'AND',
      estimatedSize: estimatedSize ?? 0,
      createdBy:     user?.uid || null,
      createdAt:     serverTimestamp(),
      updatedAt:     serverTimestamp(),
    });
    return docRef.id;
  };

  /**
   * Update an existing audience document.
   * Always stamps updatedAt.
   */
  const updateAudience = async (audienceId, data) => {
    if (!companyId) throw new Error('No companyId');
    const docRef = doc(db, 'companies', companyId, 'audiences', audienceId);
    await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
  };

  /**
   * Hard-delete an audience document.
   */
  const deleteAudience = async (audienceId) => {
    if (!companyId) throw new Error('No companyId');
    const docRef = doc(db, 'companies', companyId, 'audiences', audienceId);
    await deleteDoc(docRef);
  };

  return (
    <AudienceContext.Provider value={{
      audiences,
      loading,
      allQuotes,
      allInvoices,
      ensureAudienceSources,
      createAudience,
      updateAudience,
      deleteAudience,
    }}>
      {children}
    </AudienceContext.Provider>
  );
};

export default AudienceContext;
